import cookieReader from 'js-cookie';
import store from '@xarvio/frontend-core-state/lib/configureStore';
import { decodeToken } from '@xarvio/frontend-core-state/lib/auth/utils';
import { GigyaResponseType } from '@xarvio/frontend-core-state/lib/auth/types';
import type { datadogRum as DatadogRumType } from '@datadog/browser-rum';
import type {
  RumEvent,
  RumActionEventDomainContext,
  RumEventDomainContext,
} from '@datadog/browser-rum-core';
import { isFeatureEnabled } from '../components/shared/FeatureToggle/utils';

export type LocalStorageCookieConsent = {
  timestamp: string;
  isAtInternetEnabled?: boolean;
  isDatadogEnabled?: boolean;
  appVersion: string;
};

export const DATADOG_COOKIE_LIST = ['_dd_s'];

export const AT_INTERNET_COOKIE_LIST = [
  'atidvisitor',
  'atreman',
  'atredir',
  'atsession',
  'atuserid',
  'atauthority',
  'attvtreman',
  'attvtsession',
  'atwebosession',
];

const DATADOG_ACTION_NAME_ATTRIBUTES = ['data-selenium', 'data-key'] as const;

// Singleton promise: guards against double-init and caches the loaded instance
const datadogRumLoader = (() => {
  let instance: Promise<typeof DatadogRumType> | null = null;

  return {
    load: (initFn: (rum: typeof DatadogRumType) => void) => {
      if (!instance) {
        instance = import('@datadog/browser-rum').then(({ datadogRum }) => {
          initFn(datadogRum);
          return datadogRum;
        });
      }
      return instance;
    },
    get: () => instance,
  };
})();

export const getDatadogInstance = () => datadogRumLoader.get();

const isRumActionEventDomainContext = (
  context: RumEventDomainContext,
): context is RumActionEventDomainContext => 'events' in context;

const getAttributeValue = (element: Element, attribute: string) => {
  const elementsWithAttribute = [
    element.closest(`[${attribute}]`),
    element.querySelector(`[${attribute}]`),
  ];

  for (const elementWithAttribute of elementsWithAttribute) {
    const attributeValue = elementWithAttribute
      ?.getAttribute(attribute)
      ?.trim();

    if (attributeValue) {
      return attributeValue;
    }
  }

  return undefined;
};

export const getPreferredDatadogActionName = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return undefined;
  }

  for (const attribute of DATADOG_ACTION_NAME_ATTRIBUTES) {
    const attributeValue = getAttributeValue(target, attribute);

    if (attributeValue) {
      return attributeValue;
    }
  }

  return undefined;
};

const getPreferredDatadogActionNameFromContext = (
  context: RumEventDomainContext,
) => {
  if (!isRumActionEventDomainContext(context)) {
    return undefined;
  }

  for (const actionEvent of context.events ?? []) {
    const actionName = getPreferredDatadogActionName(actionEvent.target);

    if (actionName) {
      return actionName;
    }
  }

  return undefined;
};

export const applyPreferredDatadogActionName = (
  event: RumEvent,
  context: RumEventDomainContext,
) => {
  if (event.type !== 'action' || !event.action.target) {
    return;
  }

  const preferredActionName = getPreferredDatadogActionNameFromContext(context);

  if (preferredActionName) {
    event.action.target.name = preferredActionName;
  }
};

const handleBeforeSend = (event: RumEvent, context: RumEventDomainContext) => {
  const token = store.getState().auth.user.token;
  const { country, uuid } = decodeToken(token);

  event.context = {
    ...event.context,
    ...(country && { country }),
    ...(uuid && { userUuid: uuid }),
  };

  if (event.view.url) {
    const url = new URL(event.view.url);
    const urlPathname = url.pathname.replace(/^\/[a-z]{2}\/[a-z_]+/, '');
    url.pathname = urlPathname;
    event.view.url = url.toString();
  }

  if (event.view.referrer) {
    const referrer = new URL(event.view.referrer);
    const reffererPathName = referrer.pathname.replace(
      /^\/[a-z]{2}\/[a-z_]+/,
      '',
    );
    referrer.pathname = reffererPathName;
    event.view.referrer = referrer.toString();
  }

  applyPreferredDatadogActionName(event, context);

  return true;
};

/**
 * Determines sessionReplaySampleRate based on device capabilities.
 * High-end devices get full replay, low-end devices get reduced or none.
 */
const getAdaptiveReplaySampleRate = (): number => {
  const cores = navigator.hardwareConcurrency ?? 0;
  const memoryGb =
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 0;

  // High-end: 8+ cores and 8+ GB RAM
  if (cores >= 8 && memoryGb >= 8) {
    return 100;
  }

  // Mid-range: 4+ cores and 4+ GB RAM
  if (cores >= 4 && memoryGb >= 4) {
    return 50;
  }

  // Low-end or unknown (deviceMemory not supported in Firefox/Safari)
  // Use cores alone as fallback when memory info is unavailable
  if (memoryGb === 0) {
    if (cores >= 8) return 50;
    if (cores >= 4) return 20;
    return 5;
  }

  return 5;
};

export const getDatadogAllowedTrackingOrigins = () => [
  // Uncomment the env variable VITE_DATADOG_APPLICATION_ID to enable on localhost for testing
  import.meta.env.VITE_IS_REMOTE_DEPLOYMENT === 'true'
    ? import.meta.env.VITE_SERVER_PUBLIC_API_HOST
    : `https://${import.meta.env.VITE_HOST}:${import.meta.env.VITE_PORT}`,
  import.meta.env.VITE_SERVER_MAIN_API_HOST,
];

export const loadDatadog = () => {
  if (import.meta.env.VITE_ENVIRONMENT === 'LOCAL') {
    return;
  }

  return datadogRumLoader.load((datadogRum) => {
    datadogRum.init({
      applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
      clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.eu',
      service: 'xfm-web',
      env: import.meta.env.VITE_DATADOG_ENV,
      version: import.meta.env.VITE_APP_VERSION,
      sessionSampleRate: 100,
      defaultPrivacyLevel: 'allow',
      allowedTrackingOrigins: getDatadogAllowedTrackingOrigins(),
      sessionReplaySampleRate: getAdaptiveReplaySampleRate(),
      trackResources: false,
      trackLongTasks: true,
      trackUserInteractions: true,
      actionNameAttribute: 'data-key',
      beforeSend: handleBeforeSend,
    });
  });
};

export const loadAtInternet = () => {
  if (import.meta.env.VITE_AT_INTERNET_ID) {
    const myScript = document.createElement('script');
    myScript.setAttribute('fetchpriority', 'low');
    myScript.setAttribute(
      'src',
      `https://tag.aticdn.net/${
        import.meta.env.VITE_AT_INTERNET_ID
      }/smarttag.js`,
    );
    document.body.appendChild(myScript);

    const onAtInternetScriptLoaded = () => {
      console.info('AT Internet script loaded');
      myScript.removeEventListener('load', onAtInternetScriptLoaded);
    };

    myScript.addEventListener('load', onAtInternetScriptLoaded, false);
  }
};

type SyncAnalyticsStateProps = {
  isAtInternetEnabled: boolean;
  isDatadogEnabled: boolean;
};

export const syncAnalyticsState = ({
  isAtInternetEnabled,
  isDatadogEnabled: isDatadogEnabledParent,
}: SyncAnalyticsStateProps) => {
  let isDatadogEnabled = isDatadogEnabledParent;

  if (
    !isFeatureEnabled('DATA_DOG_COOKIE_MODAL') ||
    !import.meta.env.VITE_DATADOG_APPLICATION_ID ||
    isFeatureEnabled('TEST_USER')
  ) {
    isDatadogEnabled = false;
  }

  if (isDatadogEnabled) {
    loadDatadog();
  } else {
    DATADOG_COOKIE_LIST.forEach((cookie) => {
      cookieReader.remove(cookie);
    });
  }
  if (isAtInternetEnabled) {
    loadAtInternet();
  } else {
    AT_INTERNET_COOKIE_LIST.forEach((cookie) => {
      cookieReader.remove(cookie);
    });
  }
};

export const setDdUser = async (user: GigyaResponseType) => {
  const instance = getDatadogInstance();
  if (!instance) {
    return;
  }
  const datadogRum = await instance;
  datadogRum.setUser({
    id: user.UID,
    name: `${user.profile.firstName} ${user.profile.lastName}`,
    email: user.profile.email,
  });
};
